<template>
    <Container>
        <div class="my-4 flex flex-wrap items-center justify-between gap-3">
            <div>
                <h1 class="text-2xl font-bold text-white">Artist Battles</h1>
                <p class="mt-1 text-sm text-nomad-300">Create matchups, manage voting windows, and review results.</p>
            </div>
            <Link href="/backstage/artist-battles/create"
                class="rounded-md bg-nomad-accent px-4 py-2 font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80">
                Create Battle
            </Link>
        </div>

        <p v-if="loadError" class="mb-4 rounded-lg bg-red-500/10 p-4 text-sm text-red-300">{{ loadError }}</p>
        <p v-if="loading" class="rounded-xl bg-nomad-900 p-6 text-nomad-300">Loading battles...</p>
        <p v-else-if="!battles.length" class="rounded-xl bg-nomad-900 p-6 text-nomad-300">
            No artist battles yet. Create the first matchup to get started.
        </p>

        <ul v-else class="grid gap-4">
            <li v-for="battle in battles" :key="battle.id" class="rounded-xl bg-nomad-900 p-4 sm:p-6">
                <div class="flex flex-wrap items-start justify-between gap-3">
                    <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                            <h2 class="text-lg font-semibold text-white">{{ battle.title }}</h2>
                            <span class="rounded-full px-3 py-1 text-xs font-semibold capitalize"
                                :class="statusClass(battle.status)">{{ battle.status }}</span>
                        </div>
                        <p class="mt-1 whitespace-pre-line text-sm text-nomad-300">{{ battle.description }}</p>
                    </div>
                    <span class="shrink-0 text-sm text-nomad-300">{{ battle.total_votes }} votes</span>
                </div>

                <div class="my-4 grid gap-3 sm:grid-cols-2">
                    <div v-for="artist in [battle.artist_one, battle.artist_two]" :key="artist.id"
                        class="flex items-center gap-3 rounded-lg bg-nomad-800 p-3">
                        <img :src="artist.image || fallbackImage" :alt="artist.name"
                            class="h-14 w-14 shrink-0 rounded-lg object-cover" />
                        <div class="min-w-0 flex-1">
                            <p class="truncate font-semibold text-white">{{ artist.stage_name || artist.name }}</p>
                            <p class="text-sm text-nomad-300">{{ artist.vote_count }} votes · {{ artist.percentage }}%</p>
                        </div>
                        <span v-if="battle.winner_artist_id === artist.id"
                            class="rounded-full bg-nomad-accent/15 px-2 py-1 text-xs font-semibold text-nomad-accent">
                            Winner
                        </span>
                    </div>
                </div>

                <div class="flex flex-wrap gap-x-5 gap-y-1 text-xs text-nomad-400">
                    <span>Starts: {{ formatDate(battle.starts_at) }}</span>
                    <span>Ends: {{ formatDate(battle.ends_at) }}</span>
                </div>

                <div v-if="battle.status === 'draft'" class="mt-4 rounded-lg bg-nomad-800 p-4">
                    <p class="mb-3 text-sm font-medium text-nomad-200">Schedule this draft</p>
                    <div class="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                        <label class="text-xs font-medium text-nomad-300">
                            Starts at
                            <input v-model="scheduleFor(battle.id).starts_at" type="datetime-local"
                                class="mt-1 block w-full rounded-md border border-nomad-700 bg-nomad-900 text-sm text-white focus:border-nomad-accent focus:ring-nomad-accent" />
                        </label>
                        <label class="text-xs font-medium text-nomad-300">
                            Ends at
                            <input v-model="scheduleFor(battle.id).ends_at" type="datetime-local"
                                class="mt-1 block w-full rounded-md border border-nomad-700 bg-nomad-900 text-sm text-white focus:border-nomad-accent focus:ring-nomad-accent" />
                        </label>
                        <button type="button" @click="scheduleBattle(battle)" :disabled="busyBattleId === battle.id"
                            class="rounded-md bg-nomad-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-nomad-600 disabled:opacity-50">
                            {{ busyBattleId === battle.id ? 'Saving...' : 'Save schedule' }}
                        </button>
                    </div>
                </div>

                <p v-if="actionErrors[battle.id]" class="mt-3 text-sm text-red-400">{{ actionErrors[battle.id] }}</p>
                <div v-if="battle.status !== 'completed'" class="mt-4 flex flex-wrap gap-2">
                    <button v-if="battle.status === 'draft'" type="button" @click="activateBattle(battle)"
                        :disabled="busyBattleId === battle.id || !battle.ends_at"
                        class="rounded-md bg-nomad-accent px-4 py-2 text-sm font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80 disabled:cursor-not-allowed disabled:opacity-40">
                        Activate now
                    </button>
                    <button v-if="battle.status === 'active'" type="button" @click="endBattle(battle)"
                        :disabled="busyBattleId === battle.id"
                        class="rounded-md border border-nomad-700 px-4 py-2 text-sm font-semibold text-red-300 transition-colors hover:border-red-400 disabled:opacity-50">
                        End battle
                    </button>
                </div>
            </li>
        </ul>

        <div v-if="pagination.last_page > 1" class="mt-5 flex items-center justify-between">
            <button type="button" @click="loadBattles(pagination.current_page - 1)"
                :disabled="loading || pagination.current_page <= 1"
                class="rounded-md bg-nomad-900 px-4 py-2 text-sm text-white disabled:opacity-40">
                Previous
            </button>
            <span class="text-sm text-nomad-300">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
            <button type="button" @click="loadBattles(pagination.current_page + 1)"
                :disabled="loading || pagination.current_page >= pagination.last_page"
                class="rounded-md bg-nomad-900 px-4 py-2 text-sm text-white disabled:opacity-40">
                Next
            </button>
        </div>
    </Container>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import axios from 'axios';
import Container from '@/Pages/Backstage/Container.vue';

const fallbackImage = 'https://open.nomadlyrics.com/assets/images/no-artist.png';
const battles = ref([]);
const loading = ref(false);
const loadError = ref('');
const busyBattleId = ref(null);
const actionErrors = ref({});
const schedules = reactive({});
const pagination = reactive({ current_page: 1, last_page: 1 });

onMounted(() => loadBattles());

function scheduleFor(id) {
    if (!schedules[id]) schedules[id] = { starts_at: '', ends_at: '' };
    return schedules[id];
}

async function loadBattles(page = 1) {
    loading.value = true;
    loadError.value = '';
    try {
        const response = await axios.get('/api/backstage/artist-battles', { params: { page } });
        battles.value = response.data.data || [];
        pagination.current_page = response.data.meta?.current_page || 1;
        pagination.last_page = response.data.meta?.last_page || 1;
    } catch (error) {
        loadError.value = error.response?.data?.message || 'Unable to load artist battles. Please refresh and try again.';
    } finally {
        loading.value = false;
    }
}

async function runAction(battle, action, payload = {}) {
    busyBattleId.value = battle.id;
    actionErrors.value[battle.id] = '';
    try {
        await action(payload);
        await loadBattles(pagination.current_page);
    } catch (error) {
        const validationErrors = error.response?.data?.errors;
        const validationMessage = validationErrors
            ? Object.values(validationErrors).flat()[0]
            : null;
        actionErrors.value[battle.id] = validationMessage || error.response?.data?.message || 'Unable to update this battle.';
    } finally {
        busyBattleId.value = null;
    }
}

function scheduleBattle(battle) {
    const schedule = scheduleFor(battle.id);
    if (!schedule.starts_at || !schedule.ends_at) {
        actionErrors.value[battle.id] = 'Choose both a start time and an end time.';
        return;
    }
    runAction(battle, (data) => axios.put(`/api/backstage/artist-battles/${battle.id}/schedule`, data), {
        starts_at: schedule.starts_at,
        ends_at: schedule.ends_at,
    });
}

function activateBattle(battle) {
    runAction(battle, () => axios.post(`/api/backstage/artist-battles/${battle.id}/activate`));
}

function endBattle(battle) {
    if (!window.confirm(`End "${battle.title}" now? The winner will be calculated from current votes.`)) return;
    runAction(battle, () => axios.post(`/api/backstage/artist-battles/${battle.id}/end`));
}

function formatDate(value) {
    return value ? new Date(value).toLocaleString() : 'Not scheduled';
}

function statusClass(status) {
    return {
        draft: 'bg-nomad-700 text-nomad-200',
        active: 'bg-nomad-accent/15 text-nomad-accent',
        completed: 'bg-nomad-blue/10 text-nomad-blue',
    }[status] || 'bg-nomad-700 text-nomad-200';
}
</script>
